import type { FC, FormEvent } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Link, Separator, Typography } from "@aviary";
import { AnalyticsEvent, useAnalyticsLogger, useBreakpoints } from "@shared/hooks";
import { useSearchParams } from "@shared/react-router-dom";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { l } from "@unauthenticated/shared/locales/i18n";

import { PageOne } from "./PageOne";
import { isFormValid } from "./PageOne/helpers";
import { PageTwo } from "./PageTwo";
import type { SimplifySignUpWithSignUpPageProps } from "./types";

import * as styles from "./SimplifySignUp.styles";

// pass formData and setFormData to pages, allow fields to call setFormData

const SignUpPageSelector: FC<SimplifySignUpWithSignUpPageProps> = props => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const [searchParams, setSearchParams] = useSearchParams();

  const step = searchParams.get("step");

  const isValidStep = () => {
    return step === "1" || step === "2";
  };

  useEffect(() => {
    if (
      !isValidStep() ||
      (step === "2" && !isFormValid(props.formData, props.isExplicitTosExperiment))
    ) {
      searchParams.set("step", "1");
      setSearchParams(searchParams);
    } else if (step === "1") {
      props.setFormData(prevData => ({ ...prevData, tosTimestamp: "" }));
    }
  }, [step]);

  const logAnalyticsEvent = useAnalyticsLogger();

  const getPage = () => {
    if (step === "2") return <PageTwo {...props} />;

    return <PageOne onClick={onClick} {...props} />;
  };

  const getNextPage = () => {
    if (step === "2") return "1";
    else return "2";
  };

  const onClick = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nextStep = getNextPage();
    searchParams.set("step", nextStep);
    setSearchParams(searchParams);
    logAnalyticsEvent({
      eventName: AnalyticsEvent.STORE_SIGNUP_TO_SIGNUP_METHOD,
      payload: {
        utm_campaign: searchParams.get("utm_campaign") || "",
        utm_medium: searchParams.get("utm_medium") || "",
        utm_source: searchParams.get("utm_source") || "",
        page_version: "simplify_sign_up",
        account_modality: props.formData.practitionerType.description,
      },
    });
  }; // todo remove this when we have forms controlling the pages.

  const renderSignInLink = () => {
    return (
      <Arrange justify="center" data-testid="form-sign-in-link">
        <Typography isMarginless>
          {t(l.common.HaveAcount)}{" "}
          <Link isColor="primary" to={authRoutes.login}>
            {t(l.common.SignIn)}
          </Link>
        </Typography>
      </Arrange>
    );
  };

  const logImAPatientEvent = () => {
    logAnalyticsEvent({
      eventName: AnalyticsEvent.STORE_SIGNUP_IM_A_PATIENT,
      payload: {
        utm_campaign: searchParams.get("utm_campaign") || "",
        utm_medium: searchParams.get("utm_medium") || "",
        utm_source: searchParams.get("utm_source") || "",
      },
    });
  };

  const renderPatientLink = () => {
    return (
      <Arrange css={styles.patientLink} justify="center" data-testid="patient-sign-up-link">
        <Typography isMarginless>
          {t(l.common.IAmAPatient)}{" "}
          <Link isColor="primary" to={authRoutes.patient_sign_up} onClick={logImAPatientEvent}>
            {t(l.common.FindInvite)}
          </Link>
        </Typography>
      </Arrange>
    );
  };

  const renderSeparator = () => (
    <div css={styles.dividerBox}>
      <Separator />
    </div>
  );

  return (
    <>
      {getPage()}
      {tablet.lessThan && renderSignInLink()}
      {!tablet.lessThan && step === "1" && renderSeparator()}
      {step === "1" && renderPatientLink()}
    </>
  );
};

export { SignUpPageSelector };
